import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useSteamStatus } from './steamstatus/SteamStatus';
import './sidebar.css';

/** Open external-tournament notifications (servers waiting to be attached) — polled. */
function ExtBadge() {
    const [n, setN] = useState(0);
    useEffect(() => {
        let alive = true;
        const load = () => axios.get('/api/v1/ext/summary').then(r => { if (alive) setN(r.data.open_notifications || 0); }).catch(() => { });
        load();
        const iv = setInterval(load, 20000);
        return () => { alive = false; clearInterval(iv); };
    }, []);
    if (!n) return null;
    return <span className="sb-count">{n}</span>;
}

/** "Update available" pill for super admins on managed installs — polled every 10 min. */
function UpdatePill() {
    const { t } = useTranslation();
    const [latest, setLatest] = useState(null);
    useEffect(() => {
        let alive = true;
        const load = () => axios.get('/api/update/status').then(r => {
            const u = r.data.managed && r.data.updater;
            if (alive) setLatest(u && u.available && u.status !== 'updating' ? u.latest : null);
        }).catch(() => { });
        load();
        const iv = setInterval(load, 600000);
        return () => { alive = false; clearInterval(iv); };
    }, []);
    if (!latest) return null;
    return (
        <NavLink to="/admin/settings/updates" className="sb-update">
            {t('sidebar.updateAvailable', 'Update available: v{{v}}', { v: latest })}
        </NavLink>
    );
}

// ─── JWT helpers ──────────────────────────────────────────────────────────────
function getJwtExpiryDaysRemaining() {
    try {
        const token = localStorage.getItem('token');
        if (!token) return null;
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (!payload.exp) return null;
        const expiresAt = payload.exp * 1000; // Convert seconds to ms
        const now = Date.now();
        const remaining = expiresAt - now;
        if (remaining <= 0) return 0;
        return Math.ceil(remaining / (1000 * 60 * 60 * 24)); // Days remaining
    } catch {
        return null;
    }
}

// The sidebar is mounted again on every page: the last known states live here, so a page change
// shows them at once instead of "Reconnecting" / "Checking" until the new socket / poll answers.
const LOST_GRACE_MS = 3000; // a lost connection is only shown after it stays lost this long
let lastWsStatus = null;
let lastApiStatus = null;

// ─── WebSocket status hook ────────────────────────────────────────────────────
function useWsStatus() {
    const [status, setStatus] = useState(lastWsStatus || 'connected');
    const wsRef = useRef(null);
    const retryRef = useRef(null);
    const retryCount = useRef(0);
    const lostRef = useRef(null);

    useEffect(() => {
        let destroyed = false;

        // not open within the grace time (first connect or after a drop) → "Reconnecting"
        function armLost() {
            if (lostRef.current) return;
            lostRef.current = setTimeout(() => {
                lostRef.current = null;
                if (!destroyed) { lastWsStatus = 'reconnecting'; setStatus('reconnecting'); }
            }, LOST_GRACE_MS);
        }

        function connect() {
            if (destroyed) return;
            const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
            const host = window.location.hostname;
            const rawPort = window.location.port;
            const port = rawPort === '3000' ? '5000' : (rawPort || '');
            const portSuffix = port ? `:${port}` : '';
            const token = localStorage.getItem('token');
            const ws = new WebSocket(`${proto}://${host}${portSuffix}/ws${token ? `?token=${token}` : ''}`);
            wsRef.current = ws;
            ws.onopen = () => {
                if (destroyed) return;
                clearTimeout(lostRef.current); lostRef.current = null;
                lastWsStatus = 'connected'; setStatus('connected'); retryCount.current = 0;
            };
            ws.onmessage = (event) => {
                try {
                    const msg = JSON.parse(event.data);
                    if (msg.type === 'tech_pause' && msg.data) {
                        window.dispatchEvent(new CustomEvent('techPause', { detail: msg.data }));
                    }
                } catch (_) { }
            };
            ws.onclose = () => {
                if (!destroyed) {
                    armLost();
                    const delay = Math.min(500 * 2 ** retryCount.current, 3000);
                    retryCount.current += 1;
                    retryRef.current = setTimeout(connect, delay);
                }
            };
            ws.onerror = () => ws.close();
        }

        armLost();
        connect();
        return () => {
            destroyed = true;
            clearTimeout(retryRef.current);
            clearTimeout(lostRef.current);
            if (wsRef.current) { wsRef.current.onclose = null; wsRef.current.close(); }
        };
    }, []);

    return status;
}

// ─── /api/status polling hook (Redis + LogAddress) ───────────────────────────
function useApiStatus() {
    const [redis, setRedis] = useState(lastApiStatus ? lastApiStatus.redis : 'checking');
    const [logReceiver, setLogReceiver] = useState(lastApiStatus ? lastApiStatus.logReceiver : 'checking');

    useEffect(() => {
        let timer = null;
        let destroyed = false;
        let failedAt = 0; // when the panel stopped answering (0 = it answers)

        const show = (r, l) => {
            if (destroyed) return;
            lastApiStatus = { redis: r, logReceiver: l };
            setRedis(r); setLogReceiver(l);
        };

        async function poll() {
            try {
                const res = await fetch('/api/status');
                if (!res.ok) throw new Error('non-200');
                const data = await res.json();
                failedAt = 0;
                show(data.redis?.ok ? 'connected' : 'unavailable', data.logReceiver?.ok ? 'running' : 'down');
                timer = setTimeout(poll, 10000);
            } catch {
                // panel not answering: keep the last state, ask again in 3 s, show "Error" once it stays down
                if (!failedAt) failedAt = Date.now();
                else if (Date.now() - failedAt >= LOST_GRACE_MS) show('error', 'error');
                timer = setTimeout(poll, LOST_GRACE_MS);
            }
        }

        poll();
        return () => { destroyed = true; clearTimeout(timer); };
    }, []);

    return { redis, logReceiver };
}

// ─── Status word colours ────────────────────────────────────────────────────
const DOT_COLORS = {
    connected: 'var(--sb-green)',
    reconnecting: 'var(--sb-amber)',
    configured: 'var(--sb-green)',
    running: 'var(--sb-green)',
    unavailable: 'var(--sb-red)',
    down: 'var(--sb-red)',
    'not configured': 'var(--text-muted)',
    checking: 'var(--text-muted)',
    error: 'var(--sb-red)',
    disconnected: 'var(--sb-red)',
};

// Map raw status tokens (some contain spaces) to sidebar.status.* i18n keys
const STATUS_KEYS = {
    connected: 'connected',
    reconnecting: 'reconnecting',
    configured: 'configured',
    running: 'running',
    unavailable: 'unavailable',
    down: 'down',
    'not configured': 'notConfigured',
    checking: 'checking',
    error: 'error',
    disconnected: 'disconnected',
};

/** One service row: name, then its state as a coloured word (no dot). */
function StatusDot({ label, status, t }) {
    const fallback = status.charAt(0).toUpperCase() + status.slice(1);
    const statusKey = STATUS_KEYS[status];
    const statusLabel = statusKey ? t(`sidebar.status.${statusKey}`, fallback) : fallback;
    return (
        <>
            <dt>{label}</dt>
            <dd style={{ '--sb-tone': DOT_COLORS[status] || 'var(--text-muted)' }}>{statusLabel}</dd>
        </>
    );
}

// ─── Section label ──────────────────────────────────────────────────────────
function SectionLabel({ children }) {
    return <div className="sb-section">{children}</div>;
}

// ─── Sidebar ──────────────────────────────────────────────────────────────────
function Sidebar() {
    const { t } = useTranslation();
    const { logout, user } = useAuth();
    const navigate = useNavigate();
    const wsStatus = useWsStatus();
    const { redis, logReceiver } = useApiStatus();
    // Steam Status link: green / amber / red dot (nothing while unknown, e.g. no Steam key)
    const { data: steam } = useSteamStatus();
    const steamDot = steam && {
        good: { color: 'var(--accent-green)', title: t('steamStatus.allGood', 'CS2 and Steam sign-in are working') },
        minor: { color: 'var(--accent-amber)', title: t('steamStatus.minor', 'Degraded') },
        major: { color: 'var(--accent-red)', title: t('steamStatus.major', 'Down') },
    }[steam.overall];

    const handleLogout = () => {
        logout();
        navigate('/admin/login');
    };

    const [mobileOpen, setMobileOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    const closeMobile = () => { if (isMobile) setMobileOpen(false); };

    const isPanelAdmin = user?.role === 'panel_admin';
    const isSuperAdmin = user?.role === 'super_admin' || user?.role === 'admin';
    const isSubAdmin = user?.is_subadmin === true;
    const canManageAdmins = isSuperAdmin || (isPanelAdmin && !isSubAdmin);

    // ─── JWT expiry state ─────────────────────────────────────────────
    const [daysRemaining, setDaysRemaining] = useState(getJwtExpiryDaysRemaining());

    // Update days remaining every hour
    useEffect(() => {
        const updateDays = () => setDaysRemaining(getJwtExpiryDaysRemaining());
        const interval = setInterval(updateDays, 60 * 60 * 1000); // Every hour
        return () => clearInterval(interval);
    }, []);

    return (
        <>
            <button className="hamburger-btn" onClick={() => setMobileOpen(prev => !prev)} aria-label={mobileOpen ? t('sidebar.closeMenu', 'Close menu') : t('sidebar.openMenu', 'Open menu')}>
                {mobileOpen ? '✕' : '☰'}
            </button>
            <div className={`sidebar-overlay${mobileOpen ? ' sidebar-overlay-visible' : ''}`} onClick={() => setMobileOpen(false)} />
            <div className={`sidebar${mobileOpen ? ' sidebar-open' : ''}`}>
                {/* Brand */}
                <div className="sb-brand">
                    <img src="/logo.png" alt="" />
                    <b>Flux</b>
                    <span>Panel</span>
                </div>

                {/* Navigation */}
                <nav className="sb-navlist">
                    <SectionLabel>{t('nav.sectionOverview', 'Overview')}</SectionLabel>
                    <NavLink to="/admin/dashboard" onClick={closeMobile} className="sb-nav">
                        <svg aria-hidden="true" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                        </svg>
                        {t('nav.dashboard', 'Dashboard')}
                    </NavLink>

                    <SectionLabel>{t('nav.sectionCompetition', 'Competition')}</SectionLabel>
                    <NavLink to="/admin/matches" onClick={closeMobile} className="sb-nav">
                        <svg aria-hidden="true" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M14.707 9.293a1 1 0 00-1.414 0L10 12.586l-2.293-2.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l4-4a1 1 0 000-1.414z M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                        </svg>
                        {t('nav.matches', 'Matches')}
                    </NavLink>
                    <NavLink to="/admin/matches/new" onClick={closeMobile} className="sb-create">
                        <svg aria-hidden="true" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                        </svg>
                        {t('nav.createMatch', 'Create Match')}
                    </NavLink>
                    <NavLink to="/admin/teams" onClick={closeMobile} className="sb-nav">
                        <svg aria-hidden="true" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        {t('nav.teams', 'Teams')}
                    </NavLink>
                    <NavLink to="/admin/seasons" onClick={closeMobile} className="sb-nav">
                        <svg aria-hidden="true" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {t('nav.seasons', 'Seasons')}
                    </NavLink>

                    <SectionLabel>{t('nav.sectionExternal', 'External')}</SectionLabel>
                    <NavLink to="/admin/tournaments" onClick={closeMobile} className="sb-nav">
                        <svg aria-hidden="true" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M8 21h8m-4-4v4m-5-17h10v5a5 5 0 01-10 0V4zM7 6H4a3 3 0 003 4m10-4h3a3 3 0 01-3 4" />
                        </svg>
                        {t('nav.tournaments', 'Tournaments')}
                        <ExtBadge />
                    </NavLink>

                    <SectionLabel>{t('nav.sectionConfiguration', 'Configuration')}</SectionLabel>
                    <NavLink to="/admin/servers" onClick={closeMobile} className="sb-nav">
                        <svg aria-hidden="true" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
                        </svg>
                        {t('nav.servers', 'Servers')}
                    </NavLink>
                    <NavLink to="/admin/maps" onClick={closeMobile} className="sb-nav">
                        <svg aria-hidden="true" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                        </svg>
                        {t('nav.maps', 'Maps')}
                    </NavLink>
                    <NavLink to="/admin/settings" onClick={closeMobile} className="sb-nav">
                        <svg aria-hidden="true" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        {t('nav.settings', 'Settings')}
                    </NavLink>

                    <SectionLabel>{t('nav.sectionSystem', 'System')}</SectionLabel>
                    {canManageAdmins && (
                        <NavLink to="/admin/admins" onClick={closeMobile} className="sb-nav">
                            <svg aria-hidden="true" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                            </svg>
                            {t('nav.admins', 'Admins')}
                        </NavLink>
                    )}
                    <NavLink to="/admin/api" onClick={closeMobile} className="sb-nav">
                        <svg aria-hidden="true" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                        </svg>
                        {t('nav.api', 'API')}
                    </NavLink>
                    <NavLink to="/admin/wiki" onClick={closeMobile} className="sb-nav">
                        <svg aria-hidden="true" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                        </svg>
                        {t('nav.wiki', 'Wiki')}
                    </NavLink>
                    <NavLink to="/admin/steam-status" onClick={closeMobile} className="sb-nav">
                        <svg aria-hidden="true" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 12h4l3-8 4 16 3-8h4" />
                        </svg>
                        {t('nav.steamStatus', 'Steam Status')}
                        {steamDot && (
                            <span className="sb-signal" title={steamDot.title} role="img" aria-label={steamDot.title} style={{ '--sb-tone': steamDot.color }} />
                        )}
                    </NavLink>
                    <NavLink to="/admin/vrs-rankings" onClick={closeMobile} className="sb-nav">
                        <svg aria-hidden="true" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                        {t('nav.vrsRankings', 'VRS Ranking')}
                    </NavLink>
                </nav>

                {/* Bottom section: user, status, logout */}
                <div className="sb-foot">
                    {/* User info */}
                    {user?.username && (
                        <div className="sb-user">
                            <div className="sb-avatar" aria-hidden="true">{user.username.charAt(0).toUpperCase()}</div>
                            <div className="sb-user-text">
                                <span className="sb-user-name">{user.username}</span>
                                {user.role && (
                                    <span className="sb-user-role">{(r => r.charAt(0).toUpperCase() + r.slice(1))(user.role.replace(/_/g, ' '))}</span>
                                )}
                            </div>
                        </div>
                    )}

                    {isSuperAdmin && <UpdatePill />}

                    {/* Service status: name, then its state as a coloured word */}
                    <dl className="sb-services">
                        <StatusDot label={t('sidebar.websocket', 'WebSocket')} status={wsStatus} t={t} />
                        <StatusDot label={t('sidebar.logReceiver', 'Log Receiver')} status={logReceiver} t={t} />
                        <StatusDot label={t('sidebar.redis', 'Redis')} status={redis} t={t} />
                    </dl>

                    {/* Version + Build */}
                    <div className="sb-version">
                        <b>v{require('../../package.json').version}</b>
                        {process.env.REACT_APP_BUILD_COMMIT && (
                            <code title={t('sidebar.buildCommit', 'Commit this build was made from')}>{process.env.REACT_APP_BUILD_COMMIT}</code>
                        )}
                        {process.env.REACT_APP_BUILD_TIME && <code>{process.env.REACT_APP_BUILD_TIME}</code>}
                    </div>

                    {/* JWT Expiry */}
                    {daysRemaining !== null && (
                        <div className="sb-session"
                            title={t('sidebar.sessionTooltip', "Your login session expires after 30 days. You'll need to re-login when this reaches 0.")}
                            style={{ '--sb-tone': daysRemaining <= 3 ? 'var(--sb-red)' : daysRemaining <= 7 ? 'var(--sb-amber)' : 'var(--text-secondary)' }}>
                            <svg aria-hidden="true" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>{t('sidebar.session', 'Session: {{count}} days left', { count: daysRemaining })}</span>
                        </div>
                    )}

                    {/* Logout */}
                    <button type="button" className="sb-signout" onClick={handleLogout}>
                        <svg aria-hidden="true" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        {t('sidebar.signOut', 'Sign out')}
                    </button>
                </div>
            </div>

            {/* Pulse animations */}
            <style>{`
                @keyframes wsPulse {
                    0%, 100% { transform: scale(1); opacity: 0.4; }
                    50%       { transform: scale(2.4); opacity: 0; }
                }
            `}</style>

        </>
    );
}

export default Sidebar;
